import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingScreen } from '../shared/LoadingScreen';
import { logger } from '@deskbay/shared';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { session, restaurantData, availableRestaurants, isLoading, hasInitialized, authResolved, error } = useAuth();
    const location = useLocation();

    // Mostra loading durante verifica auth o se non ancora inizializzato
    // Questo previene il flash redirect a onboarding durante il caricamento iniziale
    if (isLoading || !hasInitialized) {
        return <LoadingScreen />;
    }

    // Se c'è un errore di caricamento, mostra un messaggio invece di redirect loop
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[100dvh] p-4 text-center">
                <h1 className="text-xl font-bold text-red-600 mb-2">Si è verificato un errore</h1>
                <p className="text-slate-600 mb-4">Impossibile caricare i dati del ristorante. Riprova tra poco.</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
                >
                    Ricarica pagina
                </button>
            </div>
        );
    }

    // Non loggato → redirect a welcome (intro stories + onboarding)
    //
    // IMPORTANTE: `session === null` è concludente solo dopo che il listener auth si è
    // pronunciato (`authResolved`). Il safety timer di AuthContext può forzare
    // hasInitialized=true mentre la lettura del token da Keychain/EncryptedSharedPreferences
    // è ancora in corso: senza questo controllo un utente con sessione valida veniva
    // sbattuto su /welcome e sembrava sloggato. `authResolved` ha comunque un timeout
    // proprio (12s), quindi qui non si può restare bloccati.
    if (!session) {
        if (!authResolved) {
            return <LoadingScreen />;
        }
        return <Navigate to="/welcome" state={{ from: location }} replace />;
    }

    // Loggato ma senza dati ristorante ancora disponibili.
    // IMPORTANTE: distinguiamo tra "utente senza ristorante" e "dati ancora in caricamento".
    // Il safety timer in AuthContext (3s) può scattare prima che loadAppData completi,
    // impostando hasInitialized=true con restaurantData=null. Se l'utente HA ristoranti
    // (availableRestaurants.length > 0) ma il dettaglio non è ancora arrivato,
    // mostriamo ancora il LoadingScreen invece di mandarlo a /onboarding (falso positivo).
    if (!restaurantData) {
        if (availableRestaurants.length > 0) {
            return <LoadingScreen />;
        }
        return <Navigate to="/onboarding" replace />;
    }

    // Block access to dashboard if onboarding is not completed
    // This prevents bypass via browser back button
    // Only check for /dashboard routes to avoid redirect loops
    if (location.pathname.startsWith('/dashboard') &&
        restaurantData.onboarding_status &&
        restaurantData.onboarding_status !== 'completed') {
        logger.auth.warn(`Blocking dashboard access - onboarding not completed: ${restaurantData.onboarding_status}`, 'ProtectedRoute');
        return <Navigate to="/onboarding" replace />;
    }

    // Tutto OK → mostra contenuto
    return <>{children}</>;
}
